// Single source of truth for the /tools hub: title, slug, audience,
// description, related tools, and the downstream CTA. Add a tool here only
// once its page is actually implemented — the hub and the SEO/sitemap
// generator both read this list, so an entry with no page ships a dead card
// or a 404 in the sitemap.
//
// `implemented: false` entries (none yet) would be excluded from the hub by
// getImplementedTools() below — the extension point for adding a new tool
// without hand-editing the hub page.

/** @type {import("./types").FreeToolsRegistryEntry[]} */
export const FREE_TOOLS_REGISTRY = [
  {
    slug: "resume-interview-questions",
    showInHub: false,
    title: "Resume-to-Interview Prep Pack",
    shortTitle: "Resume → Interview Questions",
    audience: "Candidates",
    description:
      "Paste your resume and a target job description to get a requirement-by-requirement evidence map, relevant practice questions with follow-ups, and real experiences worth preparing.",
    metaDescription:
      "Free tool: paste your resume and a job description to get relevant practice questions, a requirement-by-requirement evidence map, and experiences worth preparing.",
    articleSlug: "resume-to-interview-preparation-pack",
    cta: {
      label: "Practise these questions with an AI interviewer.",
      // Candidates practise on Just One Interview. The page links there with
      // CANDIDATE_PRACTICE_URL (lib/candidateCta.ts, see
      // components/free-tools/tool-content.tsx); this file is plain JS read by
      // the build scripts, so it carries the address itself.
      destination: "https://app.justoneinterview.com/login",
    },
    related: ["interview-kit-generator", "boolean-search-generator"],
    implemented: true,
  },
  {
    slug: "interview-kit-generator",
    title: "Job-Description-to-Interview Kit",
    shortTitle: "Interview Kit Generator",
    audience: "Recruiters and hiring managers",
    description:
      "Paste a job description to get a competency list, a timed interview outline, questions with follow-ups, and a behaviour-anchored scorecard — an editable draft, not a validated assessment.",
    metaDescription:
      "Free tool: turn a job description into an editable interview kit — competencies, a timed outline, questions, and a scorecard with rating anchors.",
    articleSlug: "job-description-interview-kit-guide",
    cta: {
      label: "Use this kit in AIIA",
      // Existing recruiter create-role flow. Requires an authenticated
      // recruiter (hr) account — see components/free-tools/interview-kit-content.tsx's
      // auth-aware ctaHref, which falls back to /auth/login?type=hr.
      destination: "/dashboard/agency/roles/new",
    },
    related: ["job-description-generator", "reference-check-questions"],
    implemented: true,
  },
  {
    slug: "boolean-search-generator",
    title: "Recruiter Boolean Search Builder",
    shortTitle: "Boolean Search Builder",
    audience: "Recruiters",
    description:
      "Build a broad and a narrow Boolean search from a role title, skills, and exclusions — with a deterministic query compiler for Google X-ray and LinkedIn search, and optional job-description suggestions to review.",
    metaDescription:
      "Free tool: compile a broad and narrow Boolean search for Google X-ray or LinkedIn from your role title, skills, and exclusions — no scraping, no automated searches.",
    articleSlug: "recruiter-boolean-search-builder-guide",
    // Secondary CTA only. There is no sourcing integration — this points at
    // the existing recruiter create-role flow for interviewing candidates you
    // found yourself, and never creates or saves anything on its own.
    cta: {
      label: "Set up a role in AIIA",
      destination: "/dashboard/agency/roles/new",
    },
    related: ["job-description-generator", "interview-kit-generator"],
    implemented: true,
  },
  {
    slug: "cost-per-hire-calculator",
    title: "Cost per Hire Calculator",
    shortTitle: "Cost per Hire Calculator",
    audience: "Recruiters and hiring managers",
    description:
      "Calculate cost per hire from your recruiting spend and hires, with a separate screening workload estimate. Your amounts stay in the browser.",
    metaDescription: "Calculate cost per hire from your recruiting spend and hires, with a separate screening workload estimate. Your amounts stay in the browser.",
    articleSlug: "cost-per-hire-calculator-guide",
    cta: {
      label: "Set up a role in AIIA",
      destination: "/dashboard/agency/roles/new",
    },
    related: ["interview-kit-generator", "boolean-search-generator"],
    implemented: true,
  },
  {
    slug: "job-description-generator",
    title: "Job Description Generator",
    shortTitle: "Job Description Generator",
    audience: "Recruiters and hiring managers",
    description:
      "Turn your role notes into an editable job description with source-linked responsibilities, separate optional suggestions, and clear questions to resolve before posting.",
    metaDescription:
      "Free job description generator for employers. Turn role notes into an editable draft with source-linked responsibilities and requirements to review before posting.",
    articleSlug: "job-description-generator-guide",
    cta: {
      label: "Create a role in AIIA",
      destination: "/dashboard/agency/roles/new",
    },
    related: ["interview-kit-generator", "boolean-search-generator"],
    implemented: true,
  },
  {
    slug: "reference-check-questions",
    title: "Reference Check Question Kit",
    shortTitle: "Reference Check Question Kit",
    audience: "Recruiters and hiring managers",
    description: "Turn a job description into reference questions with follow-ups and a blank notes sheet. Review the draft before a human-led reference conversation.",
    metaDescription: "Free reference-check question kit for employers. Draft job-related questions, follow-ups and a blank notes sheet from your job description.",
    articleSlug: "reference-check-questions-guide",
    cta: { label: "Set up an interview in AIIA", destination: "/dashboard/agency/roles/new" },
    related: ["interview-kit-generator", "job-description-generator"],
    implemented: true,
  },
  {
    slug: "ai-resume-checker",
    title: "Free AI Resume Checker: Estimate AI Writing Likelihood",
    shortTitle: "AI Resume Checker",
    audience: "Recruiters",
    description:
      "Paste or upload a resume to get a percentage estimate of how AI written it sounds, with the exact lines flagged and why. An estimate, never a verdict.",
    metaDescription:
      "Paste or upload a resume. Get a percentage estimate of how AI written it sounds, with flagged phrases and reasons. Free. We do not store your resume.",
    articleSlug: null,
    cta: { label: "Try AI Interview Agents", destination: "/dashboard/agency/roles/new" },
    related: ["interview-kit-generator", "reference-check-questions"],
    implemented: true,
  },
];

// Exactly the tool routes that exist today: the hub plus one page per
// implemented tool. Derived from the registry so a new tool page cannot be
// added without its public route coming along.
const FREE_TOOL_PATHS = new Set([
  "/tools",
  ...FREE_TOOLS_REGISTRY.filter((tool) => tool.implemented).map(
    (tool) => `/tools/${tool.slug}`
  ),
]);

/**
 * True only for an implemented free-tool route (trailing slash tolerated).
 * Deliberately an exact match, not a "/tools" prefix test: an unknown
 * /tools/<slug> or a lookalike like /toolshed is NOT a public tool page and
 * keeps whatever routing it has today.
 */
export function isFreeToolPath(pathname) {
  if (typeof pathname !== "string") return false;
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  return FREE_TOOL_PATHS.has(normalized);
}

export function getFreeTool(slug) {
  return FREE_TOOLS_REGISTRY.find((tool) => tool.slug === slug) ?? null;
}

export function getImplementedTools() {
  return FREE_TOOLS_REGISTRY.filter((tool) => tool.implemented);
}
